/**
 * Centralized Radar scoring module.
 *
 * Used by:
 *  - radar-evaluate.tsx  (live chart normalisation)
 *  - radar-results.tsx   (per-criterion gap bars)
 *  - server/routes.ts    (mirrors same algorithm for server-side completion)
 */

export interface CriterionScoringInput {
  criterionId: string;
  value: number;
  min: number;
  max: number;
  weight: number;
}

/**
 * Normalise a raw criterion value to 0–100 on its own scale.
 */
export function normaliseCriterion(value: number, min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 0;
  return Math.round(((value - min) / range) * 100);
}

/**
 * Compute the weighted overall score (0–100) across all criteria.
 */
export function calculateWeightedScore(inputs: CriterionScoringInput[]): number {
  let weightedSum = 0;
  let weightSum = 0;
  for (const { value, min, max, weight } of inputs) {
    const normalised = normaliseCriterion(value, min, max);
    weightedSum += normalised * weight;
    weightSum += weight;
  }
  return weightSum > 0 ? Math.round(weightedSum / weightSum) : 0;
}

export type MatchOutcome = "match" | "near_threshold" | "mismatch";

/**
 * Classify a score against a match threshold.
 * - match          : score >= threshold
 * - near_threshold : score >= threshold - 10
 * - mismatch       : score < threshold - 10
 */
export function getMatchOutcome(score: number, threshold: number): MatchOutcome {
  if (score >= threshold) return "match";
  if (score >= threshold - 10) return "near_threshold";
  return "mismatch";
}

/**
 * Determine which gap-text variant applies to a normalised criterion score.
 */
export function getGapVariant(normalised: number, threshold: number): "strong" | "near" | "miss" {
  if (normalised >= threshold) return "strong";
  if (normalised >= threshold - 15) return "near";
  return "miss";
}

/**
 * Build chart data suitable for recharts RadarChart from criteria and current responses.
 * Pass `upToStep` to restrict chart to answered criteria (live wizard use).
 */
export function buildChartData(
  criteria: { id: string; name: string; minValue?: number | null; maxValue?: number | null }[],
  responses: Record<string, number>,
  upToStep?: number,
): { subject: string; value: number; fullMark: number }[] {
  return criteria.map((c, idx) => {
    const answered =
      responses[c.id] !== undefined &&
      (upToStep === undefined || idx <= upToStep);
    return {
      subject: c.name.length > 14 ? c.name.slice(0, 14) + "…" : c.name,
      value: answered ? normaliseCriterion(responses[c.id], c.minValue ?? 0, c.maxValue ?? 10) : 0,
      fullMark: 100,
    };
  });
}
